import {useCallback, useEffect, useState} from "react";
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Select,
    Tooltip,
    Typography
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import LineChartBase from "./LineChartBase";
import SearchIcon from "@mui/icons-material/Search";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import {graphFetch} from "../../api/fetchConfig";
import {format} from "date-fns";
import {ko} from "date-fns/locale";


const PopupChart = ({ open, onClose }) => {
    const [range, setRange] = useState("day");
    const [date, setDate] = useState(new Date());
    const [data, setData] = useState([]);
    const [lines, setLines] = useState([]);

    const handleSearch = () => {
        const query = `
          query getPopupPowerData($filter: KpiFilter) {
            getPopupPowerData(filter: $filter) {
              timeLabel
              label
              value
            }
          }
        `;

        graphFetch(
            query,
            {
                filter: {
                    "date" : format(date, 'yyyy-MM-dd'),
                    "range" : range
                }
            }
        ).then((data) => {
            if (data.errors) {
                console.error('Error:', data.errors);
            } else {
                const result = data.getPopupPowerData;
                const deviceIds = new Set();
                const isHourBased = range === "day";

                // 1. 시간별로 그룹핑
                const grouped = {};

                result.forEach(item => {
                    const { timeLabel, label, value } = item;

                    if (!grouped[timeLabel]) {
                        grouped[timeLabel] = { timeLabel };
                    }
                    grouped[timeLabel][label] = Number(parseFloat(value).toFixed(2));
                    deviceIds.add(item.label);
                });

                const sorted = Object.values(grouped).sort((a, b) => {
                    if (isHourBased) {
                        return Number(a.timeLabel) - Number(b.timeLabel);
                    } else {
                        return new Date(a.timeLabel) - new Date(b.timeLabel);
                    }
                });
                setData(sorted);

                const availableColors = ["blue", "deeppink", "green", "orange", "purple", "red"];
                const newLines = Array.from(deviceIds).map((deviceId, idx) => ({
                    key: deviceId,
                    color: availableColors[idx % availableColors.length]
                }));
                setLines(newLines);
            }
        }).catch((err) => {
            console.error('Fetch error:', err);
        });
    };

    useEffect(() => {
        if (open) {
            handleSearch();
        }
    }, [open]);

    useEffect(() => {
        if (open && date && range) {
            handleSearch();
        }
    }, [date, range, open]);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">📊 상세 보기</Typography>

                    <Box display="flex" alignItems="center" gap={1}>
                        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ko}>
                            <DatePicker
                                label="조회일자"
                                value={date}
                                onChange={(newDate) => setDate(newDate)}
                                slotProps={{ textField: { size: "small" } }}
                            />
                        </LocalizationProvider>

                        <Select
                            size="small"
                            value={range}
                            onChange={(e) => setRange(e.target.value)}
                        >
                            <MenuItem value="day">일간</MenuItem>
                            <MenuItem value="week">주간</MenuItem>
                            <MenuItem value="month">월간</MenuItem>
                        </Select>

                        <Button
                            variant="contained"
                            startIcon={<SearchIcon />}
                            size="small"
                            onClick={handleSearch}
                        >
                            조회
                        </Button>
                    </Box>
                </Box>
            </DialogTitle>
            <DialogContent sx={{ padding: 2 }}>
                <Alert
                    severity="info"
                    icon={<InfoIcon sx={{ color: '#1976d2' }} />}
                    sx={{ mb: 2, backgroundColor: '#e3f2fd', border: '1px solid #bbdefb' }}
                >
                    <Tooltip
                        title={
                            <Box>
                                <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                                    📊 전력 수치 안내
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    표시되는 전력값은 평균으로 계산된 값입니다.
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    • <strong>일간:</strong> 시간별 평균 전력
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    • <strong>주간:</strong> 일별 평균 전력
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    • <strong>월간:</strong> 일별 평균 전력
                                </Typography>
                                <Typography variant="body2">
                                    설비의 간헐적 동작으로 인해 최대 전력보다 낮게 표시될 수 있습니다.
                                </Typography>
                            </Box>
                        }
                        placement="top"
                        arrow
                    >
                        <Typography variant="body2" sx={{ cursor: 'help' }}>
                            💡 표시되는 전력값은 전력의 <strong>순간최대치</strong>가 아닌 <strong>평균값</strong>입니다.
                            자세한 내용은 여기에 마우스를 올려보세요.
                        </Typography>
                    </Tooltip>
                </Alert>

                {data.length > 0 && (
                    <Box sx={{ width: '100%', height: '400px' }}>
                        <LineChartBase
                            data={data}
                            lines={lines}
                            xAxisLabel={range === "day" ? "시간(h)" : "일자(d)"}
                            yAxisLabel="전력 (W)"
                        />
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default PopupChart;